import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Phone, Plus, Minus } from "lucide-react";
import { CAMPUS_DETAILS, CONTACT } from "../../data/homeData";
import { PROGRAMS_CATALOG } from "../../data/programsCatalog";
import { CAMPUSES } from "../../data/staticPagesData";

// Footer modeled after andersoncollege.ca production site.
// Matches the user-provided reference image: 3 columns on dark charcoal —
//   1. 140+ YEARS shield-seal logo (white),
//   2. Two-paragraph intro copy,
//   3. "CAMPUS LOCATIONS" with expandable "+ City" rows.
// A slim legal strip sits below for copyright + policy links.

// X (formerly Twitter) — lucide doesn't ship the new wordmark yet, so we
// render the brand-correct glyph as a small inline SVG component.
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.16 17.52h1.833L7.084 4.126H5.117L17.084 19.77Z" />
  </svg>
);
XIcon.displayName = "XIcon";

// Ordered alphabetically per user request (2026-05-03).
const FOOTER_CAMPUSES = [
  "Brampton",
  "Brantford",
  "Downtown Toronto",
  "Hamilton",
  "Kitchener",
  "London",
  "Mississauga – Dundas",
  "Mississauga – Hurontario",
  "North York",
  "Windsor",
];

// TikTok — lucide ships no TikTok icon; render the official brand glyph
// as a small inline SVG component (same pattern as XIcon above).
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M21.55 6.83a6.34 6.34 0 0 1-3.96-1.4 6.34 6.34 0 0 1-2.36-3.93h-3.4v13.4c0 1.65-1.34 3-3 3a3 3 0 1 1 0-6c.34 0 .67.06.97.16v-3.48a6.45 6.45 0 0 0-.97-.07 6.45 6.45 0 1 0 6.4 6.45V8.99a9.71 9.71 0 0 0 6.32 2.31V7.9c-.01 0-.01-.07 0-1.07Z" />
  </svg>
);
TikTokIcon.displayName = "TikTokIcon";

// Shared social-profile list — footer, homepage Organization `sameAs`,
// and every campus's `sameAs` all import from this module so they
// cannot drift. See /app/frontend/src/data/social.js for the rule.
import { SOCIAL_PROFILES } from "../../data/social";

const SOCIAL_ICONS = { Facebook, X: XIcon, LinkedIn: Linkedin, Instagram, TikTok: TikTokIcon, YouTube: Youtube };
const SOCIAL_LINKS = SOCIAL_PROFILES.map((p) => ({ ...p, Icon: SOCIAL_ICONS[p.label] }));

// Per-campus Google Business / Maps direct URLs (provided by Anderson 2026-06-01).
// Clicking the expanded address now opens the campus's actual Google profile
// rather than a generic "search for X" query, which was unreliable for some
// addresses (the search would land on a competing or merged business).
const CAMPUS_GOOGLE_URLS = {
  "Brampton":                  "https://maps.app.goo.gl/ji8dZjYruQGKk9RL7",
  "Brantford":                 "https://maps.app.goo.gl/B4PaXgzjFRhzZYkA9",
  "Downtown Toronto":          "https://share.google/inuZ27xKOfxTTgj80",
  "Hamilton":                  "https://maps.app.goo.gl/594jHzMqv8uLbtRy6",
  "Kitchener":                 "https://maps.app.goo.gl/Y5YJrFoEZG4szvm48",
  "London":                    "https://maps.app.goo.gl/Y5YJrFoEZG4szvm48",
  "Mississauga – Dundas":      "https://maps.app.goo.gl/421UNnFpiHnwpLoc8",
  "Mississauga – Hurontario":  "https://maps.app.goo.gl/28VVoEFNd27hdDan8",
  "North York":                "https://share.google/weXbcjPxYx7d2s02q",
  "Windsor":                   "https://maps.app.goo.gl/KayLELmNZGWeuYPz7",
};

const CampusRow = ({ name }) => {
  const [open, setOpen] = useState(false);
  const details = CAMPUS_DETAILS.find((c) => c.name === name);
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  // Prefer the per-campus Google Business URL provided by the client.
  // Fall back to a Maps search if a campus row is added later without a
  // dedicated URL — protects against link rot if the seed map ever drifts
  // out of sync with FOOTER_CAMPUSES.
  const mapsHref = CAMPUS_GOOGLE_URLS[name]
    || (details
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            `Anderson College ${name} ${details.addr1} ${details.addr2}`
          )}`
        : null);
  return (
    <div className="border-b border-white/10 last:border-b-0" data-testid={`footer-campus-${slug}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-start gap-3 py-3 min-h-[44px] text-left hover:text-white transition group"
      >
        <span className="text-white/85 w-4 h-4 flex items-center justify-center shrink-0">
          {open ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
        </span>
        <span className="text-white font-semibold text-[15px] md:text-[16px] tracking-wide">
          {name.replace("Downtown Toronto", "Toronto")}
        </span>
      </button>
      {open && details && (
        <a
          href={mapsHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`footer-campus-${slug}-maps`}
          className="block pb-4 pl-7 text-[13.5px] leading-[1.7] text-white/70 hover:text-white transition ac-fade-up text-left"
        >
          <div>{details.addr1}</div>
          <div>{details.addr2}</div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#c8a96a] mt-1.5">
            Open in Google Maps →
          </div>
        </a>
      )}
    </div>
  );
};

const Footer = () => {
  return (
    <footer id="campuses" data-testid="site-footer" className="bg-[#353535] text-white">
      <div className="ac-container py-16 md:py-24">
        {/* 3-col grid at desktop (≥lg). At tablet (md 768-1023) becomes a
            2-col layout — logo + intro on row 1, campus list spans full
            width on row 2 — preventing the historic ~17 px overflow that
            the un-stepped lg→1-col jump caused on iPad portrait. */}
        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] lg:grid-cols-[200px_1fr_360px] gap-10 md:gap-12 lg:gap-16 items-start">
          {/* Column 1 — Anderson College stacked "140+ YEARS" logo */}
          <div className="flex md:block justify-center md:justify-start">
            <img
              src="/assets/brand/logo-stacked-white.png"
              alt="Anderson College — Celebrating 140+ Years"
              width={340}
              height={780}
              className="w-[140px] md:w-[160px] h-auto select-none"
              loading="lazy"
              decoding="async"
              data-testid="footer-seal"
            />
          </div>

          {/* Column 2 — intro copy (two paragraphs) */}
          <div className="text-white/80 text-[14px] md:text-[15px] leading-[1.95] space-y-5 max-w-2xl text-center md:text-left">
            <p>
              Anderson College is one of Canada's oldest colleges, having opened our first location
              in 1885. We have trained more than 150,000 students over 140+ years, and have
              been an empowering partner for students overcoming career challenges and transitioning
              to the next phase of their lives.
            </p>
            <p>
              With ten locations in Ontario, we are recognized for our high-calibre faculty,
              hands-on, real-life career training and supportive culture. At Anderson College, we
              are more than our century-old history, our programs and state-of-the-art facilities;
              we are a team of caring individuals, deeply committed to helping our students become
              skillful, confident and in-demand employees.
            </p>
          </div>

          {/* Column 3 — Campus locations accordion (spans both cols at md, own col at lg) */}
          <div data-testid="footer-campus-list" className="text-left md:col-span-2 lg:col-span-1">
            <h3
              className="text-white text-[18px] md:text-[20px] font-bold uppercase tracking-[0.1em] mb-4 text-left"
              style={{ fontFamily: "var(--font-display)" }}
              data-testid="footer-campuses-heading"
            >
              Campus Locations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1">
              {FOOTER_CAMPUSES.map((name) => (
                <CampusRow key={name} name={name} />
              ))}
            </div>
          </div>
        </div>

        {/* Thin connect strip — phone + socials (centered on mobile) */}
        <div className="mt-14 md:mt-16 pt-8 border-t border-white/10 flex flex-col items-center lg:flex-row gap-6 lg:justify-between">
          <a
            href={CONTACT.phoneHref}
            className="inline-flex items-center gap-2 text-white hover:text-[#f5c056] text-[15px] font-semibold transition min-h-[44px] px-1"
            data-testid="footer-phone"
          >
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
          <div className="flex items-center gap-2" data-testid="footer-socials">
            {SOCIAL_LINKS.map(({ Icon, href, label }, i) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-11 h-11 rounded-md bg-white/10 text-white flex items-center justify-center hover:bg-white hover:text-[#2a2a2a] transition"
                data-testid={`footer-social-${i}`}
              >
                <Icon className="w-[16px] h-[16px]" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Crawlable sitemap block — real <Link> anchors so search engines
         can discover every program + campus page from the homepage (the
         header nav is JS dropdowns and invisible to crawlers). */}
      <div className="border-t border-white/10" data-testid="footer-explore">
        <div className="ac-container py-8">
          <div className="text-[12px] uppercase tracking-[0.16em] font-bold text-white/85 mb-3">
            Explore Programs
          </div>
          <ul className="columns-2 sm:columns-3 lg:columns-4 gap-x-6 text-[13.5px] leading-relaxed" data-testid="footer-explore-programs-list">
            {PROGRAMS_CATALOG.map((p) => (
              <li key={p.slug} className="break-inside-avoid">
                <Link
                  to={`/programs/${p.slug}`}
                  className="inline-flex items-center min-h-[44px] py-1 text-white/65 hover:text-white transition"
                  data-testid={`footer-program-${p.slug}`}
                >
                  {p.name}
                </Link>
              </li>
            ))}
          </ul>
          <div className="text-[12px] uppercase tracking-[0.16em] font-bold text-white/85 mt-7 mb-3">
            Our Campuses
          </div>
          <ul className="columns-2 sm:columns-3 lg:columns-5 gap-x-6 text-[13.5px] leading-relaxed" data-testid="footer-explore-campuses-list">
            {CAMPUSES.map((c) => (
              <li key={c.slug} className="break-inside-avoid">
                <Link
                  to={`/campuses/${c.slug}`}
                  className="inline-flex items-center min-h-[44px] py-1 text-white/65 hover:text-white transition"
                  data-testid={`footer-campus-${c.slug}`}
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Legal strip — typography bumped per UX-audit 2026-05-30 Theme 4
         (footer link minimum 13.5px from prior 12px, link tap targets
         min-h-[44px] for AAA touch compliance). */}
      <div className="border-t border-white/10">
        <div className="ac-container py-5 text-[13.5px] text-white/65 flex flex-col md:flex-row gap-3 md:justify-between items-center">
          <div className="leading-relaxed">
            © {new Date().getFullYear()} Anderson College of Health, Business and Technology. All rights reserved.
          </div>
          <div className="flex items-center gap-x-5 gap-y-1 flex-wrap justify-center">
            <Link to="/events" className="inline-flex items-center min-h-[44px] hover:text-white transition" data-testid="footer-events">Events</Link>
            <Link to="/student-portal" className="inline-flex items-center min-h-[44px] hover:text-white transition" data-testid="footer-student-portal">Student Portal</Link>
            <Link to="/careers" className="inline-flex items-center min-h-[44px] hover:text-white transition" data-testid="footer-careers">Work at Anderson</Link>
            <Link to="/sitemap" className="inline-flex items-center min-h-[44px] hover:text-white transition" data-testid="footer-sitemap">Sitemap</Link>
            <Link to="/terms" className="inline-flex items-center min-h-[44px] hover:text-white transition" data-testid="footer-terms">Terms of Use</Link>
            <Link to="/privacy" className="inline-flex items-center min-h-[44px] hover:text-white transition" data-testid="footer-privacy">Privacy Policy</Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("ac:open-cookie-preferences"))}
              className="inline-flex items-center min-h-[44px] hover:text-white transition"
              data-testid="footer-cookie-preferences"
            >
              Cookie Preferences
            </button>
            {/* Brand Guidelines link temporarily removed per Sampath (2026-06-19).
                Re-enable by uncommenting the <a> block below when ready. */}
            {false && (
              <a
                href={`${(typeof window !== 'undefined' && window.location && window.location.origin ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/brand-guidelines.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center min-h-[44px] hover:text-white transition"
                data-testid="footer-brand-guidelines"
              >
                Brand Guidelines (PDF)
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
